// "Enviar para o My Planner?" (texto 10, E): o estado guardado (documento "envios", sincronizado).
// Só lê e grava o documento; não importa as definições das abas (assim modulos/agendaAuto pode usar sem ciclo).
// Regra fixa: nada de outras abas vai sozinho para o My Planner (exceto remédios e consultas aceitas). Isto é só um lembrete.
import { useSyncExternalStore } from 'react';
import type { AbaId, AguardandoConf, EnvioPendente, EnviosDoc, Task } from '../store/types';
import { getDoc, setDoc, useDoc } from '../store/store';
import { t } from './i18n';

export const chaveEnvio = (aba: AbaId, id: string) => `${aba}:${id}`;
/** Abas com dados de saúde (E4): no My Planner vai só o título, o horário e o nome do profissional. */
export const ABAS_SIGILO: AbaId[] = ['saude', 'consultorio'];

const doc = (): EnviosDoc => { const d = getDoc('envios'); return { ...d, pend: d?.pend ?? {} }; };
const mudar = (fn: (d: EnviosDoc) => EnviosDoc) => setDoc('envios', d => fn({ ...d, pend: d?.pend ?? {} }));

// ─── pendentes ("Me lembre depois") ───
export function marcarPendente(p: Omit<EnvioPendente, 'criado' | 'vezes'>) {
  mudar(d => ({ ...d, pend: { ...d.pend, [chaveEnvio(p.aba, p.id)]: { ...p, criado: new Date().toISOString(), vezes: 0, quando: null } } }));
}
/** Enviado ou "Não, obrigada": o aviso não volta para este item. */
export function encerrarPendente(chave: string) {
  if (!doc().pend[chave]) return;
  mudar(d => { const { [chave]: _x, ...resto } = d.pend; return { ...d, pend: resto }; });
}
export function mudarPendente(chave: string, fn: (p: EnvioPendente) => EnvioPendente) {
  mudar(d => (d.pend[chave] ? { ...d, pend: { ...d.pend, [chave]: fn(d.pend[chave]) } } : d));
}
export const pendentes = () => Object.entries(doc().pend);

// ─── "Lembrar minha escolha nesta aba" ───
export const escolhaDaAba = (aba: AbaId) => doc().escolha?.[aba];
export function lembrarEscolha(aba: AbaId, e: 'enviar' | 'depois' | 'nao' | null) {
  mudar(d => { const n = { ...(d.escolha ?? {}) }; if (e) n[aba] = e; else delete n[aba]; return { ...d, escolha: n }; });
}

// ─── "⏳ Aguardando confirmação" (E3) ───
export const estaAguardando = (aba: AbaId, id: string) => !!doc().aguardando?.[chaveEnvio(aba, id)];
export function useAguardando(aba?: AbaId | null, id?: string | null) {
  const d = useDoc('envios');
  return aba && id ? d?.aguardando?.[chaveEnvio(aba, id)] ?? null : null;
}
export function marcarAguardando(a: Omit<AguardandoConf, 'desde'>) {
  mudar(d => ({ ...d, aguardando: { ...(d.aguardando ?? {}), [chaveEnvio(a.aba, a.id)]: { ...a, desde: new Date().toISOString() } } }));
}
export function lembrarDeConfirmar(aba: AbaId, id: string, quando: Date | null) {
  const k = chaveEnvio(aba, id);
  mudar(d => (d.aguardando?.[k] ? { ...d, aguardando: { ...d.aguardando, [k]: { ...d.aguardando[k], lembrar: quando ? quando.toISOString() : null } } } : d));
}
/** "Confirmar": tira a etiqueta e a marca de provisório das tarefas ligadas a este item no My Planner. */
export function confirmarItem(aba: AbaId, id: string) {
  const k = chaveEnvio(aba, id);
  mudar(d => { const { [k]: _x, ...resto } = d.aguardando ?? {}; return { ...d, aguardando: resto }; });
  const suf = sufixoProvisorio();
  const ligadas = getDoc('planner').tasks.filter(x => x.link?.aba === aba && x.link?.id === id && x.title.endsWith(suf));
  if (ligadas.length) {
    const ids = new Set(ligadas.map(x => x.id));
    setDoc('planner', d => ({ ...d, tasks: d.tasks.map(x => (ids.has(x.id) ? { ...x, title: x.title.slice(0, -suf.length), updatedAt: new Date().toISOString() } : x)) }));
  }
}
const sufixoProvisorio = () => ' · ⏳ ' + t('provisório');

/** O que vai para o My Planner a partir de um item de outra aba (vale para todos os caminhos de envio):
 *  - E4: nas abas de saúde, só título, horário e o nome do profissional (nada de motivo, diagnóstico, observações, anexos);
 *  - E3: item "Aguardando confirmação" enviado de propósito vai com a marca de provisório no título. */
export function paraPlanner(aba: AbaId, id: string | undefined, p: Partial<Task>, prof?: string): Partial<Task> {
  let r: Partial<Task> = { ...p };
  if (ABAS_SIGILO.includes(aba)) {
    r = { ...r, notes: (prof ?? '').trim() };
    delete r.subtasks; delete r.anexos;   // nada do conteúdo da ficha vai junto
  }
  if (id && estaAguardando(aba, id) && r.title && !r.title.endsWith(sufixoProvisorio())) r.title = r.title + sufixoProvisorio();
  return r;
}

// ─── "Me lembre de confirmar" (E3): escolher a data, aberto pelo ⋮ do item ───
let confirmarPedido: { aba: AbaId; id: string } | null = null;
const ouvConf = new Set<() => void>();
export const usePedidoConfirmar = () => useSyncExternalStore(cb => { ouvConf.add(cb); return () => { ouvConf.delete(cb); }; }, () => confirmarPedido, () => null);
export const pedirDataConfirmar = (a: { aba: AbaId; id: string } | null) => { confirmarPedido = a; ouvConf.forEach(f => f()); };
